import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function SiemPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("logs"); // logs | alerts
  const [q, setQ] = useState("");
  const [severity, setSeverity] = useState("all");
  const [category, setCategory] = useState("all");
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);

  const loadLogs = async () => {
    const params = { q, limit: 500 };
    if (severity !== "all") params.severity = severity;
    if (category !== "all") params.category = category;
    const { data } = await api.get("/siem/logs", { params });
    setLogs(data);
  };
  const loadAlerts = async () => {
    const { data } = await api.get("/siem/alerts");
    setAlerts(data);
  };

  useEffect(() => { loadLogs(); loadAlerts(); }, [user?.mode]);

  const ack = async (a) => {
    await api.post(`/siem/alerts/${a._id}/acknowledge`);
    toast.success("Alerte acquittée");
    loadAlerts();
  };

  return (
    <div className="space-y-6" data-testid="siem-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
          {t("siem")}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1">Security Operations Center</h1>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === "logs" ? "default" : "ghost"}
                data-testid="tab-logs"
                onClick={() => setTab("logs")}
                className={tab === "logs" ? "bg-cyan-500 text-black hover:bg-cyan-400" : "text-white/70 hover:bg-white/5"}>
          {t("logs")}
        </Button>
        <Button variant={tab === "alerts" ? "default" : "ghost"}
                data-testid="tab-alerts"
                onClick={() => setTab("alerts")}
                className={tab === "alerts" ? "bg-cyan-500 text-black hover:bg-cyan-400" : "text-white/70 hover:bg-white/5"}>
          {t("alerts")} ({alerts.filter((a) => a.status === "open").length})
        </Button>
      </div>

      {tab === "logs" && (
        <>
          <Card className="bg-white/[0.02] border-white/10 rounded-sm p-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-white/40" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Rechercher (action, email, ressource…)"
                       data-testid="siem-search"
                       className="pl-8 bg-white/[0.03] border-white/10" />
              </div>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid="siem-severity" className="bg-white/[0.03] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute sévérité</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="siem-category" className="bg-white/[0.03] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toute catégorie</SelectItem>
                  <SelectItem value="auth">Auth</SelectItem>
                  <SelectItem value="pdp">PDP</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="policy">Policy</SelectItem>
                  <SelectItem value="scenario">Scenario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3">
              <Button onClick={loadLogs} data-testid="siem-search-btn"
                      size="sm" className="bg-cyan-500 text-black hover:bg-cyan-400">Rechercher</Button>
            </div>
          </Card>

          <Card className="bg-white/[0.02] border-white/10 rounded-sm overflow-hidden">
            <div className="max-h-[560px] overflow-auto">
              <table className="w-full font-mono text-[11px]">
                <thead className="sticky top-0 bg-[#0A0A0A] border-b border-white/10">
                  <tr className="text-left text-white/50 uppercase tracking-[0.2em] text-[10px]">
                    <th className="p-2">Timestamp</th>
                    <th className="p-2">Sev</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Action</th>
                    <th className="p-2">User</th>
                    <th className="p-2">Resource</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l._id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-2 text-white/50 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                      <td className="p-2"><StatusBadge status={l.severity} /></td>
                      <td className="p-2 text-cyan-300">{l.category}</td>
                      <td className="p-2 text-white/90">{l.action}</td>
                      <td className="p-2 text-white/60">{l.user_email}</td>
                      <td className="p-2 text-white/60">{l.resource_name || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {logs.length === 0 && <div className="py-8 text-center text-white/40 text-xs">Aucun événement.</div>}
            </div>
          </Card>
        </>
      )}

      {tab === "alerts" && (
        <div className="space-y-3">
          {alerts.length === 0 && (
            <Card className="bg-white/[0.02] border-white/10 rounded-sm p-8 text-center text-white/40">
              Aucune alerte.
            </Card>
          )}
          {alerts.map((a) => (
            <Card key={a._id} className="bg-white/[0.02] border-white/10 rounded-sm p-4"
                  data-testid={`alert-row-${a._id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={a.severity} />
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="mt-1 text-sm">{a.message}</div>
                  <div className="mt-1 text-[10px] font-mono text-white/40">
                    {a.kind} · {new Date(a.timestamp).toLocaleString()}
                  </div>
                </div>
                {a.status === "open" && (
                  <Button size="sm" variant="ghost"
                          data-testid={`ack-${a._id}`}
                          onClick={() => ack(a)}
                          className="text-white/70 hover:text-cyan-400 hover:bg-white/5">
                    <ShieldCheck className="h-4 w-4" /> Acquitter
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
